import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Search } from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import { apiRequest } from "../../utils/apiRequest";
import FullPageSpinner from "../../components/FullPageSpinner";

const statusClasses = {
  Closed: "bg-emerald-100 text-emerald-700",
  Open: "bg-amber-100 text-amber-700",
  Overnight: "bg-blue-100 text-blue-700",
};

const formatDisplayDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatDisplayTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

const formatHoursWorked = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === "") return "-";

  const safeMinutes = Math.max(0, Number(minutes) || 0);
  const hours = Math.floor(safeMinutes / 60);
  const mins = safeMinutes % 60;

  return `${hours}h ${String(mins).padStart(2, "0")}m`;
};

// const formatWeekHours = (minutes) => {
//   const safeMinutes = Math.max(0, Number(minutes) || 0);
//   const hours = Math.floor(safeMinutes / 60);
//   const mins = safeMinutes % 60;

//   if (mins === 0) return `${hours}h`;
//   return `${hours}h ${String(mins).padStart(2, "0")}m`;
// };

export default function AttendancePage() {
  const token = localStorage.getItem("staff_portal_token");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [status, setStatus] = useState("");

  const [attendance, setAttendance] = useState([]);
  // const [summary, setSummary] = useState({
  //   total_sessions: 0,
  //   currently_clocked_in: 0,
  //   total_hours_this_week_minutes: 0,
  // });

  const handleLoadAttendance = useCallback(async () => {
    setLoading(true);
    setError("");

    const res = await apiRequest({
      script: "admin-get-attendance",
      token,
      data: {
        search,
        date: selectedDate,
        status,
      },
    });

    setLoading(false);

    if (!res?.success) {
      setError(res?.error || "Failed to load attendance.");
      return;
    }

    setAttendance(res?.attendance || []);
    // setSummary(
    //   res?.summary || {
    //     total_sessions: 0,
    //     currently_clocked_in: 0,
    //     total_hours_this_week_minutes: 0,
    //   },
    // );
  }, [token, search, selectedDate, status]);

  useEffect(() => {
    handleLoadAttendance();
  }, [handleLoadAttendance]);

  const exportRows = useMemo(() => {
    return attendance.map((item) => ({
      staffId: item.staff_id,
      fullName: item.full_name,
      date: item.date_label,
      clockIn: item.clock_in_label,
      clockOut: item.clock_out_label,
      hoursWorked: item.hours_worked_label,
      status: item.status,
    }));
  }, [attendance]);

  const handleExport = () => {
    if (!exportRows.length) return;

    const headers = [
      "Staff ID",
      "Full Name",
      "Date",
      "Clock In",
      "Clock Out",
      "Hours Worked",
      "Status",
    ];

    const csvRows = [
      headers.join(","),
      ...exportRows.map((row) =>
        [
          row.staffId,
          row.fullName,
          row.date,
          row.clockIn,
          row.clockOut,
          row.hoursWorked,
          row.status,
        ]
          .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
          .join(","),
      ),
    ];

    const blob = new Blob([csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "attendance-export.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout title="Attendance">
      <FullPageSpinner show={loading} text="Loading attendance..." />

      <div className="space-y-6">
        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        ) : null}

        {/* <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-500">Total Shifts</p>
            <h3 className="mt-2 text-3xl font-bold text-slate-900">
              {summary.total_sessions || 0}
            </h3>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-500">Currently Clocked In</p>
            <h3 className="mt-2 text-3xl font-bold text-slate-900">
              {summary.currently_clocked_in || 0}
            </h3>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-sm text-slate-500">Total Hours This Week</p>
            <h3 className="mt-2 text-3xl font-bold text-slate-900">
              {formatWeekHours(summary.total_hours_this_week_minutes)}
            </h3>
          </div>
        </div> */}

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex w-full flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex w-full flex-col gap-4 md:flex-row md:items-center">
              <div className="relative w-full md:max-w-sm">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by staff name or ID..."
                  className="w-full rounded-xl border border-slate-300 py-3 pl-10 pr-4 text-sm outline-none focus:ring-2 focus:ring-slate-900"
                />
              </div>

              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-900"
              />

              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-slate-900"
              >
                <option value="">All Statuses</option>
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
                <option value="Overnight">Overnight</option>
              </select>
            </div>

            <button
              type="button"
              onClick={handleExport}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              <Download size={18} />
              Export
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="max-h-[600px] overflow-auto">
            <table className="w-full min-w-[950px]">
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50">
                <tr className="text-left">
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Staff
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Date
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Clock In
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Clock Out
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Hours Worked
                  </th>
                  <th className="px-6 py-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </th>
                </tr>
              </thead>

              <tbody>
                {attendance.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-10 text-center text-sm text-slate-500"
                    >
                      No attendance records found.
                    </td>
                  </tr>
                ) : (
                  attendance.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {item.full_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {item.staff_id}
                          </p>
                        </div>
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700">
                        {item.date_label ||
                          formatDisplayDate(item.attendance_date)}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700">
                        {item.clock_in_label ||
                          formatDisplayTime(item.clock_in)}
                      </td>

                      <td className="px-6 py-4 text-sm text-slate-700">
                        {item.clock_out_label ||
                          formatDisplayTime(item.clock_out)}
                      </td>

                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {item.hours_worked_label ||
                          (item.status === "Open"
                            ? "Active"
                            : formatHoursWorked(item.total_minutes))}
                      </td>

                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
                            statusClasses[item.status] ||
                            "bg-slate-100 text-slate-700"
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
